import { useEffect, useRef, useState } from 'react';
import { MOTION } from './landingTokens';
import { recentScrollJump, meaningfullyInViewport } from './scrollJump';

// ─── useInView ────────────────────────────────────────────────────────────────
// Shared in-view state for scroll-triggered component animation (ring sweep,
// bar growth, checkmark draw). Mirrors Reveal's scroll dynamics exactly:
// - fires once the element rises past MOTION.trigger (~25% up the viewport);
// - hysteresis: resets only when FULLY off-screen (never un-animates visibly);
// - direction-aware: `instant` is true when entering from above (scroll-up),
//   so consumers render their final state with no transition.
// Under prefers-reduced-motion it reports permanently in view + instant.
// Returns [ref, inView, instant].
const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function useInView({ repeat = true, rootMargin = MOTION.trigger } = {}) {
  const ref = useRef(null);
  const [state, setState] = useState({ inView: false, instant: true });
  // Which side of the viewport the element last left from (see Reveal: sampling
  // position at the trigger callback misses fast wheel steps on small elements).
  const lastOffSide = useRef('below');

  useEffect(() => {
    if (prefersReduced()) { setState({ inView: true, instant: true }); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    // Already on screen at mount: report in-view instantly (final state, no
    // sweep) — mirrors Reveal's mount rule.
    if (meaningfullyInViewport(el)) setState({ inView: true, instant: true });
    // Trigger: fires when the element crosses the reading-zone line. Entering
    // from above (scroll-up) or right after a scroll jump reports instant.
    // `top <= 0` reads the entry side from this delivery's own geometry (see
    // Reveal — avoids depending on ioReset callback ordering).
    const ioTrigger = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const fromAbove = e.boundingClientRect.top <= 0 || lastOffSide.current === 'above';
            setState({ inView: true, instant: fromAbove || recentScrollJump() });
            if (!repeat) ioTrigger.disconnect();
          }
        });
      },
      { threshold: 0, rootMargin }
    );
    ioTrigger.observe(el);
    // Reset: separate FULL-viewport observer — the trigger observer stops
    // firing once the element leaves its shrunken root, so it can never see
    // the "fully off-screen" transition. Its positive edge doubles as the
    // jump catcher for elements landing below the trigger line (see Reveal).
    let ioReset;
    if (repeat) {
      ioReset = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) {
              lastOffSide.current = e.boundingClientRect.bottom <= 0 ? 'above' : 'below';
              setState({ inView: false, instant: true });
            } else {
              // Re-learn the side on ENTRY too — a jump can carry the element
              // across the viewport with no intersection in between (see Reveal).
              lastOffSide.current = e.boundingClientRect.top <= 0 ? 'above' : 'below';
              if (recentScrollJump() && meaningfullyInViewport(e.boundingClientRect)) setState((s) => (s.inView ? s : { inView: true, instant: true }));
            }
          });
        },
        { threshold: 0 }
      );
      ioReset.observe(el);
    }
    return () => { ioTrigger.disconnect(); if (ioReset) ioReset.disconnect(); };
  }, [repeat, rootMargin]);

  return [ref, state.inView, state.instant];
}
